// Contact Us form helpers, kept out of the component so they can be tested.
//
// The form used to turn dealers away without saying why: any rejection from
// the server read "Captcha verification failed" (there is no captcha), and a
// keyword filter refused ordinary messages like "please update my cards".

// BACs are short numbers. Dealers paste them as "123 456" or "123-456".
const BAC_RE = /^\d{1,12}$/;

// The server only stores a numeric BAC, and used to reject the whole message
// over anything else. A dealer who types their dealership's name here is still
// telling us who they are, so that goes into the message instead.
export const splitDealershipField = (value) => {
  const typed = String(value ?? '').trim();
  if (!typed) return { bac: '', note: '' };
  const digits = typed.replace(/[\s.-]/g, '');
  if (BAC_RE.test(digits)) return { bac: digits, note: '' };
  return { bac: '', note: typed };
};

// The server refuses a message over 4,000 characters (JavaScript string
// length). The box stops at 4,000, but a dealership name typed in the BAC box
// is added on the end, which could push a full message over the limit.
export const MAX_MESSAGE_LENGTH = 4000;

// Cut to at most `max` characters without leaving half of an emoji behind.
const clip = (text, max) => {
  if (text.length <= max) return text;
  const cut = text.slice(0, Math.max(0, max));
  return /[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut;
};

// The dealership note says who they are, so it is kept whole and the end of
// the message gives way instead. Only a note too long to fit on its own (far
// more than the box's 120 characters) is cut, along with everything else.
const joinMessage = (text, note) => {
  if (!note) return clip(text, MAX_MESSAGE_LENGTH);
  const tail = `\n\nDealership: ${note}`;
  const room = MAX_MESSAGE_LENGTH - tail.length;
  if (room <= 0) return clip(`${text}${tail}`, MAX_MESSAGE_LENGTH);
  return `${clip(text, room).trimEnd()}${tail}`;
};

export const buildContactPayload = ({ name, email, phone, bac, message }) => {
  const dealership = splitDealershipField(bac);
  const text = String(message ?? '').trim();
  return {
    Name: String(name ?? '').trim(),
    Email: String(email ?? '').trim(),
    Phone: String(phone ?? '').trim(),
    DealershipBAC: dealership.bac,
    Message: joinMessage(text, dealership.note),
  };
};

export const CONTACT_SENT_MESSAGE = 'Thank you for reaching out. We will get back to you soon.';

// When the "Verify you are human" check cannot run here (Cloudflare reports an
// error, the browser is not supported, or its script is blocked), the form
// still sends and the server decides. Once the server checks tokens it refuses
// a message without one, so the note does not promise the message will go: it
// gives the dealer the email address in case it does not.
export const CAPTCHA_UNAVAILABLE_MESSAGE =
  'The "Verify you are human" check could not load. If your message does not send, email adsend@martinadvertising.com.';

// The server's 400 when the check fails reads 'Please complete the "Verify you
// are human" check and try again.' (backend contactController.js
// CAPTCHA_FAILED_MESSAGE). The same way round it goes on the end.
const isCaptchaRejection = (reason) => /verify you are human/i.test(reason);
const withEmailFallback = (reason) =>
  `${reason.replace(/[.\s]+$/, '')}, or email adsend@martinadvertising.com.`;

// Tell the dealer what actually happened. A 400 carries the server's reason
// ("Email is invalid.") and that is the most useful thing to show.
export const contactErrorMessage = (err) => {
  const statusCode = err?.response?.status;
  const reason = err?.response?.data?.error;
  if (statusCode === 400 && typeof reason === 'string' && isCaptchaRejection(reason)) {
    return withEmailFallback(reason);
  }
  if (statusCode === 400 && reason) return reason;
  if (statusCode === 429) return 'Too many messages sent from here. Please wait a few minutes and try again.';
  return 'Your message could not be sent. Please try again in a few minutes.';
};
